import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── ARCADE CSS (Grades 1–5) ──────────────────────────────────────────────────
const ARCADE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Orbitron:wght@400;700;900&display=swap');

  :root {
    --bg:        #0a0a1a;
    --panel:     #12122a;
    --border:    #2a2a5a;
    --accent1:   #00f5ff;
    --accent2:   #ff006e;
    --accent3:   #ffbe0b;
    --accent4:   #06d6a0;
    --text:      #e0e0ff;
    --subtext:   #ebebed;
    --correct:   #06d6a0;
    --wrong:     #ff006e;
    --glow1: 0 0 8px #00f5ff, 0 0 20px #00f5ff44;
    --glow2: 0 0 8px #ff006e, 0 0 20px #ff006e44;
    --glow3: 0 0 8px #ffbe0b, 0 0 20px #ffbe0b44;
    --glow4: 0 0 8px #06d6a0, 0 0 20px #06d6a044;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: var(--bg); }
  .da-root {
    font-family: 'Orbitron', monospace;
    background: var(--bg);
    min-height: 100vh;
    color: var(--text);
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .da-root::before {
    content: '';
    position: fixed; inset: 0;
    background: repeating-linear-gradient(
      to bottom,
      transparent 0px, transparent 3px,
      rgba(0,0,0,0.18) 3px, rgba(0,0,0,0.18) 4px
    );
    pointer-events: none;
    z-index: 9999;
  }
  .stars { position: fixed; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
  .star {
    position: absolute;
    width: 2px; height: 2px;
    background: white;
    border-radius: 50%;
    animation: twinkle var(--d, 3s) infinite ease-in-out var(--delay, 0s);
    opacity: var(--op, 0.6);
  }
  @keyframes twinkle {
    0%,100% { opacity: var(--op, 0.6); transform: scale(1); }
    50% { opacity: 0.1; transform: scale(0.5); }
  }
  .da-hud {
    position: relative; z-index: 10;
    background: linear-gradient(180deg, #0d0d22 0%, transparent 100%);
    border-bottom: 2px solid var(--border);
    padding: 16px 24px 12px;
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }
  .da-logo {
    font-family: 'Press Start 2P', monospace;
    font-size: 13px;
    color: var(--accent1);
    text-shadow: var(--glow1);
    letter-spacing: 1px;
    white-space: nowrap;
    animation: logoFlicker 6s infinite;
  }
  @keyframes logoFlicker {
    0%,96%,100% { opacity: 1; }
    97% { opacity: 0.4; }
    98% { opacity: 1; }
    99% { opacity: 0.2; }
  }
  .da-hud-stats { display: flex; gap: 16px; flex: 1; flex-wrap: wrap; }
  .hud-chip {
    display: flex; align-items: center; gap: 8px;
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 6px 14px;
    font-size: 11px;
    letter-spacing: 0.5px;
    position: relative;
    overflow: hidden;
  }
  .hud-chip::before {
    content: '';
    position: absolute; left: 0; top: 0; bottom: 0;
    width: 3px;
    background: var(--chip-color, var(--accent1));
    box-shadow: 0 0 6px var(--chip-color, var(--accent1));
  }
  .hud-chip .label { color: var(--subtext); font-size: 9px; }
  .hud-chip .value {
    color: var(--chip-color, var(--accent1));
    font-weight: 700;
    text-shadow: 0 0 6px var(--chip-color, var(--accent1));
    font-size: 13px;
  }
  .xp-bar-wrap {
    position: relative; z-index: 10;
    padding: 8px 24px;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; gap: 12px;
  }
  .xp-label { font-family: 'Press Start 2P', monospace; font-size: 7px; color: var(--accent3); white-space: nowrap; }
  .xp-track { flex: 1; height: 12px; background: #1a1a3a; border: 1px solid var(--border); border-radius: 2px; overflow: hidden; position: relative; }
  .xp-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--accent3), var(--accent1));
    border-radius: 2px;
    transition: width 0.8s cubic-bezier(0.34,1.56,0.64,1);
    box-shadow: 0 0 10px var(--accent3);
    position: relative;
  }
  .xp-fill::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%);
    animation: xpShimmer 1.5s infinite;
  }
  @keyframes xpShimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
  .xp-count { font-size: 10px; color: var(--accent3); white-space: nowrap; min-width: 60px; text-align: right; }
  .da-arena {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: flex-start;
    padding: 24px 16px 100px;
    position: relative; z-index: 10;
    overflow-y: auto;
  }
  .q-card {
    width: 100%; max-width: 680px;
    background: var(--panel);
    border: 2px solid var(--border);
    border-radius: 12px;
    padding: 28px 28px 24px;
    position: relative;
    animation: cardIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
    box-shadow: 0 0 40px #00000088, inset 0 1px 0 #ffffff10;
  }
  @keyframes cardIn { 0% { opacity: 0; transform: translateY(30px) scale(0.95); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
  .q-card.shake { animation: cardShake 0.4s ease-in-out !important; }
  @keyframes cardShake {
    0%,100% { transform: translateX(0); }
    20% { transform: translateX(-12px) rotate(-1deg); }
    40% { transform: translateX(10px) rotate(1deg); }
    60% { transform: translateX(-8px); }
    80% { transform: translateX(6px); }
  }
  .q-card-corner { position: absolute; width: 16px; height: 16px; border-color: var(--accent1); border-style: solid; opacity: 0.6; }
  .q-card-corner.tl { top: -2px; left: -2px; border-width: 2px 0 0 2px; }
  .q-card-corner.tr { top: -2px; right: -2px; border-width: 2px 2px 0 0; }
  .q-card-corner.bl { bottom: -2px; left: -2px; border-width: 0 0 2px 2px; }
  .q-card-corner.br { bottom: -2px; right: -2px; border-width: 0 2px 2px 0; }
  .q-meta { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
  .q-num { font-family: 'Press Start 2P', monospace; font-size: 8px; color: var(--accent1); background: #00f5ff15; border: 1px solid #00f5ff44; padding: 5px 10px; border-radius: 4px; }
  .q-concept { font-size: 9px; color: var(--accent3); background: #ffbe0b15; border: 1px solid #ffbe0b44; padding: 5px 10px; border-radius: 4px; text-transform: uppercase; letter-spacing: 1px; }
  .q-text { font-size: 25px; line-height: 1.7; color: var(--text); margin: 20px 0 24px; font-family: 'Comic Sans MS', cursive; font-weight: 400; }
  .options-grid { display: flex; flex-direction: column; gap: 10px; }
  .opt-btn {
    display: flex; align-items: center; gap: 14px;
    width: 100%;
    background: #0d0d20;
    border: 2px solid var(--border);
    border-radius: 8px;
    padding: 14px 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
    color: var(--text);
    font-family: 'Comic Sans MS', cursive;
    font-size: 20px;
    text-align: left;
    opacity: 0;
    animation: optIn 0.4s ease forwards;
  }
  .opt-btn:nth-child(1) { animation-delay: 0.05s; }
  .opt-btn:nth-child(2) { animation-delay: 0.12s; }
  .opt-btn:nth-child(3) { animation-delay: 0.19s; }
  .opt-btn:nth-child(4) { animation-delay: 0.26s; }
  @keyframes optIn { 0% { opacity: 0; transform: translateX(-20px); } 100% { opacity: 1; transform: translateX(0); } }
  .opt-btn::before { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, var(--accent1) 0%, transparent 100%); opacity: 0; transition: opacity 0.3s; }
  .opt-btn:hover:not(:disabled)::before { opacity: 0.07; }
  .opt-btn:hover:not(:disabled) { border-color: var(--accent1); box-shadow: var(--glow1); transform: translateX(4px); }
  .opt-btn:disabled { cursor: not-allowed; }
  .opt-key { width: 30px; height: 30px; min-width: 30px; border: 2px solid currentColor; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-family: 'Press Start 2P', monospace; font-size: 9px; color: var(--subtext); background: #1a1a30; transition: all 0.2s; }
  .opt-btn.correct { border-color: var(--correct) !important; background: #06d6a020; animation: correctFlash 0.5s ease; box-shadow: var(--glow4) !important; }
  .opt-btn.correct .opt-key { color: var(--correct); border-color: var(--correct); background: #06d6a030; }
  @keyframes correctFlash { 0%,100% { transform: scale(1); } 30% { transform: scale(1.04); } }
  .opt-btn.wrong { border-color: var(--wrong) !important; background: #ff006e18; box-shadow: var(--glow2) !important; }
  .opt-btn.wrong .opt-key { color: var(--wrong); border-color: var(--wrong); background: #ff006e20; }
  .feedback-panel { margin-top: 20px; border-radius: 8px; padding: 16px 18px; animation: fbIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; opacity: 0; }
  @keyframes fbIn { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
  .feedback-panel.correct-fb { background: #06d6a012; border: 2px solid var(--correct); box-shadow: var(--glow4); }
  .feedback-panel.wrong-fb { background: #ff006e12; border: 2px solid var(--wrong); box-shadow: var(--glow2); }
  .fb-title { font-family: 'Press Start 2P', monospace; font-size: 10px; margin-bottom: 10px; }
  .correct-fb .fb-title { color: var(--correct); }
  .wrong-fb .fb-title { color: var(--wrong); }
  .fb-explain { font-size: 11px; color: var(--subtext); line-height: 1.7; margin-top: 8px; border-top: 1px solid var(--border); padding-top: 10px; }
  .next-row { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; gap: 12px; }
  .countdown-pill { font-family: 'Press Start 2P', monospace; font-size: 8px; color: var(--accent1); background: #00f5ff15; border: 1px solid #00f5ff40; padding: 8px 14px; border-radius: 50px; animation: cdPulse 1s infinite; }
  @keyframes cdPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
  .next-btn { font-family: 'Press Start 2P', monospace; font-size: 8px; color: var(--bg); background: var(--accent1); border: none; padding: 10px 16px; border-radius: 6px; cursor: pointer; transition: all 0.2s; box-shadow: var(--glow1); letter-spacing: 0.5px; }
  .next-btn:hover { transform: scale(1.05); }
  .fab-submit { position: fixed; bottom: 28px; right: 24px; z-index: 200; background: linear-gradient(135deg, var(--accent2), #c0006e); color: white; border: none; border-radius: 50px; padding: 14px 22px; font-family: 'Press Start 2P', monospace; font-size: 8px; cursor: pointer; box-shadow: var(--glow2), 0 10px 30px #00000060; animation: fabFloat 3s ease-in-out infinite; letter-spacing: 0.5px; display: flex; align-items: center; gap: 8px; }
  @keyframes fabFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
  .fab-submit:hover { transform: scale(1.08) translateY(-4px); }
  .loading-screen { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: 'Press Start 2P', monospace; gap: 24px; background: var(--bg); position: relative; z-index: 10; }
  .loading-title { font-size: 14px; color: var(--accent1); text-shadow: var(--glow1); }
  .loading-sub { font-size: 9px; color: var(--subtext); }
  .loading-bar-wrap { width: 280px; height: 14px; background: #1a1a3a; border: 2px solid var(--border); border-radius: 2px; overflow: hidden; }
  .loading-bar-fill { height: 100%; background: linear-gradient(90deg, var(--accent1), var(--accent4)); animation: loadProgress 2s ease-in-out infinite alternate; box-shadow: 0 0 10px var(--accent1); }
  @keyframes loadProgress { 0% { width: 10%; } 100% { width: 95%; } }
  .loading-dots::after { content: '...'; animation: dots 1.2s steps(4,end) infinite; }
  @keyframes dots { 0%,20% { content: '.'; } 40% { content: '..'; } 60%,100% { content: '...'; } }
  .result-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.92); display: flex; align-items: center; justify-content: center; z-index: 500; animation: overlayIn 0.4s ease; }
  @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
  .result-card { width: 90%; max-width: 500px; background: var(--panel); border: 2px solid var(--accent1); border-radius: 16px; padding: 36px 32px; text-align: center; box-shadow: var(--glow1), 0 30px 80px #000000aa; position: relative; overflow: hidden; animation: resultCardIn 0.6s cubic-bezier(0.34,1.56,0.64,1); }
  @keyframes resultCardIn { 0% { transform: scale(0.7) translateY(40px); opacity: 0; } 100% { transform: scale(1) translateY(0); opacity: 1; } }
  .result-card::before { content: ''; position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: conic-gradient(from 0deg, transparent, var(--accent1)22, transparent, var(--accent2)22, transparent); animation: resultSpin 8s linear infinite; }
  @keyframes resultSpin { 100% { transform: rotate(360deg); } }
  .result-inner { position: relative; z-index: 1; background: var(--panel); border-radius: 12px; padding: 28px; }
  .result-icon { font-size: 60px; animation: iconBounce 1s ease-in-out infinite alternate; display: block; margin-bottom: 12px; }
  @keyframes iconBounce { from { transform: translateY(0) rotate(-3deg); } to { transform: translateY(-14px) rotate(3deg); } }
  .result-title { font-family: 'Press Start 2P', monospace; font-size: 16px; color: var(--accent1); text-shadow: var(--glow1); margin-bottom: 6px; }
  .result-sub { font-size: 10px; color: var(--subtext); margin-bottom: 24px; }
  .stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 20px 0; }
  .stat-box { background: #0a0a18; border: 1px solid var(--border); border-radius: 8px; padding: 14px 10px; }
  .stat-box .s-label { font-size: 8px; color: var(--subtext); margin-bottom: 6px; }
  .stat-box .s-val { font-family: 'Press Start 2P', monospace; font-size: 14px; color: var(--accent3); text-shadow: var(--glow3); }
  .result-redirect { font-size: 9px; color: var(--subtext); margin-top: 20px; animation: cdPulse 1s infinite; }
  .particle-layer { position: fixed; inset: 0; pointer-events: none; z-index: 400; overflow: hidden; }
  .px-particle { position: absolute; border-radius: 2px; animation: pxFall linear forwards; }
  @keyframes pxFall { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(100vh) rotate(720deg); opacity: 0; } }
  .px-emoji { position: absolute; font-size: 28px; animation: emojiRise linear forwards; }
  @keyframes emojiRise { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(-60vh) scale(0.5); opacity: 0; } }
  .combo-flash { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); font-family: 'Press Start 2P', monospace; font-size: 28px; color: var(--accent3); text-shadow: var(--glow3); pointer-events: none; z-index: 600; animation: comboAnim 0.8s cubic-bezier(0.34,1.56,0.64,1) forwards; }
  @keyframes comboAnim { 0% { opacity: 0; transform: translate(-50%,-50%) scale(0.5); } 40% { opacity: 1; transform: translate(-50%,-55%) scale(1.2); } 70% { opacity: 1; transform: translate(-50%,-60%) scale(1); } 100% { opacity: 0; transform: translate(-50%,-80%) scale(0.8); } }
  .level-badge { font-family: 'Press Start 2P', monospace; font-size: 8px; padding: 5px 12px; border-radius: 4px; background: linear-gradient(135deg, #ffbe0b30, #ff006e20); border: 1px solid var(--accent3); color: var(--accent3); text-shadow: 0 0 6px var(--accent3); white-space: nowrap; }
  .help-btn { font-family: 'Press Start 2P', monospace; font-size: 7px; padding: 7px 12px; border-radius: 4px; background: transparent; border: 1px solid var(--subtext); color: var(--subtext); cursor: pointer; transition: all 0.2s; margin-left: auto; }
  .help-btn:hover { border-color: var(--accent1); color: var(--accent1); box-shadow: var(--glow1); }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

// ─── PROFESSIONAL CSS (Grades 6–12) ──────────────────────────────────────────
const PRO_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&family=DM+Serif+Display:ital@0;1&display=swap');

  :root {
    --bg:         #f8f7f4;
    --surface:    #ffffff;
    --surface2:   #f2f1ee;
    --border:     #e4e2dc;
    --border2:    #d0cec7;
    --text:       #1a1916;
    --subtext:    #6b6860;
    --muted:      #9e9b94;
    --accent:     #2563eb;
    --accent-bg:  #eff4ff;
    --success:    #16a34a;
    --success-bg: #f0fdf4;
    --error:      #dc2626;
    --error-bg:   #fff5f5;
    --warn:       #d97706;
    --progress:   #2563eb;
    --shadow-sm:  0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04);
    --shadow-md:  0 4px 16px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04);
    --shadow-lg:  0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06);
    --radius:     12px;
    --radius-sm:  8px;
    --radius-xs:  6px;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: var(--bg); }

  .pro-root {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    min-height: 100vh;
    color: var(--text);
    display: flex;
    flex-direction: column;
    position: relative;
  }

  /* Subtle dot grid background */
  .pro-root::before {
    content: '';
    position: fixed; inset: 0;
    background-image: radial-gradient(circle, #c8c5be 1px, transparent 1px);
    background-size: 28px 28px;
    opacity: 0.35;
    pointer-events: none;
    z-index: 0;
  }

  /* ── TOP NAV ── */
  .pro-nav {
    position: sticky; top: 0; z-index: 100;
    background: rgba(248,247,244,0.9);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    padding: 0 32px;
    height: 60px;
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .pro-brand {
    font-family: 'DM Serif Display', serif;
    font-size: 17px;
    color: var(--text);
    letter-spacing: -0.3px;
    white-space: nowrap;
  }
  .pro-brand span {
    color: var(--accent);
  }

  .pro-nav-divider {
    width: 1px; height: 20px;
    background: var(--border2);
    margin: 0 4px;
  }

  .pro-nav-meta {
    display: flex; gap: 6px; flex: 1; flex-wrap: wrap;
  }

  .pro-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: -0.1px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--subtext);
    transition: all 0.15s;
  }
  .pro-badge.accent { background: var(--accent-bg); border-color: #bfcffa; color: var(--accent); }
  .pro-badge.warn { background: #fffbeb; border-color: #fde68a; color: var(--warn); }
  .pro-badge .dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: currentColor;
  }

  .pro-progress-bar {
    height: 3px;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
    position: relative; z-index: 99;
  }
  .pro-progress-fill {
    height: 100%;
    background: var(--accent);
    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 0 8px rgba(37,99,235,0.4);
  }

  /* ── MAIN ── */
  .pro-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 36px 20px 120px;
    position: relative; z-index: 1;
    overflow-y: auto;
  }

  /* Step indicator */
  .pro-stepper {
    display: flex;
    align-items: center;
    gap: 0;
    margin-bottom: 28px;
    width: 100%;
    max-width: 640px;
  }
  .pro-step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;
  }
  .pro-step-item:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 14px;
    left: 50%;
    width: 100%;
    height: 1px;
    background: var(--border2);
    z-index: 0;
  }
  .pro-step-item:not(:last-child).done::after {
    background: var(--accent);
  }
  .pro-step-dot {
    width: 28px; height: 28px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px;
    font-weight: 600;
    border: 2px solid var(--border2);
    background: var(--surface);
    color: var(--muted);
    position: relative; z-index: 1;
    transition: all 0.3s ease;
  }
  .pro-step-item.active .pro-step-dot {
    border-color: var(--accent);
    background: var(--accent);
    color: white;
    box-shadow: 0 0 0 4px rgba(37,99,235,0.12);
  }
  .pro-step-item.done .pro-step-dot {
    border-color: var(--success);
    background: var(--success);
    color: white;
  }

  /* ── QUESTION CARD ── */
  .pro-card {
    width: 100%;
    max-width: 640px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 32px;
    box-shadow: var(--shadow-md);
    animation: proCardIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    position: relative;
  }
  @keyframes proCardIn {
    0% { opacity: 0; transform: translateY(16px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .pro-card.shake { animation: proShake 0.35s ease !important; }
  @keyframes proShake {
    0%,100% { transform: translateX(0); }
    25% { transform: translateX(-8px); }
    75% { transform: translateX(8px); }
  }

  .pro-q-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }
  .pro-q-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: var(--muted);
  }
  .pro-q-concept {
    font-size: 11px;
    font-weight: 500;
    color: var(--accent);
    background: var(--accent-bg);
    border: 1px solid #bfcffa;
    padding: 4px 10px;
    border-radius: 20px;
  }

  .pro-q-text {
    font-family: 'DM Serif Display', serif;
    font-size: 22px;
    line-height: 1.55;
    color: var(--text);
    margin-bottom: 28px;
    font-weight: 400;
    letter-spacing: -0.2px;
  }

  /* ── OPTIONS ── */
  .pro-options { display: flex; flex-direction: column; gap: 10px; }

  .pro-opt {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 14px 16px;
    cursor: pointer;
    transition: all 0.18s ease;
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    text-align: left;
    opacity: 0;
    animation: proOptIn 0.35s ease forwards;
  }
  .pro-opt:nth-child(1) { animation-delay: 0.04s; }
  .pro-opt:nth-child(2) { animation-delay: 0.1s; }
  .pro-opt:nth-child(3) { animation-delay: 0.16s; }
  .pro-opt:nth-child(4) { animation-delay: 0.22s; }
  @keyframes proOptIn { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }

  .pro-opt:hover:not(:disabled) {
    border-color: var(--accent);
    background: var(--accent-bg);
    box-shadow: var(--shadow-sm);
  }
  .pro-opt:disabled { cursor: not-allowed; }

  .pro-opt-key {
    width: 28px; height: 28px; min-width: 28px;
    border: 1.5px solid var(--border2);
    border-radius: var(--radius-xs);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px;
    font-weight: 600;
    color: var(--muted);
    background: var(--surface2);
    transition: all 0.18s;
    flex-shrink: 0;
  }

  .pro-opt.correct {
    border-color: var(--success) !important;
    background: var(--success-bg) !important;
  }
  .pro-opt.correct .pro-opt-key {
    background: var(--success); color: white; border-color: var(--success);
  }
  .pro-opt.wrong {
    border-color: var(--error) !important;
    background: var(--error-bg) !important;
  }
  .pro-opt.wrong .pro-opt-key {
    background: var(--error); color: white; border-color: var(--error);
  }

  .pro-opt-check {
    margin-left: auto;
    font-size: 16px;
    flex-shrink: 0;
  }

  /* ── FEEDBACK ── */
  .pro-feedback {
    margin-top: 18px;
    border-radius: var(--radius-sm);
    padding: 16px 18px;
    animation: proFbIn 0.4s ease forwards;
    opacity: 0;
    border-left: 3px solid;
  }
  @keyframes proFbIn { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: translateY(0); } }
  .pro-feedback.correct-fb { background: var(--success-bg); border-color: var(--success); }
  .pro-feedback.wrong-fb { background: var(--error-bg); border-color: var(--error); }

  .pro-fb-title {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
  }
  .correct-fb .pro-fb-title { color: var(--success); }
  .wrong-fb .pro-fb-title { color: var(--error); }

  .pro-fb-explain {
    font-size: 13px;
    color: var(--subtext);
    line-height: 1.65;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--border);
  }

  .pro-next-row {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 12px; gap: 12px;
  }

  .pro-countdown {
    font-size: 12px;
    color: var(--muted);
    font-weight: 500;
  }

  .pro-next-btn {
    font-size: 12px;
    font-weight: 600;
    color: var(--accent);
    background: var(--accent-bg);
    border: 1px solid #bfcffa;
    padding: 8px 14px;
    border-radius: var(--radius-xs);
    cursor: pointer;
    transition: all 0.15s;
    letter-spacing: -0.1px;
  }
  .pro-next-btn:hover {
    background: var(--accent);
    color: white;
    border-color: var(--accent);
  }

  /* ── SUBMIT BTN ── */
  .pro-fab {
    position: fixed; bottom: 24px; right: 24px;
    z-index: 200;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 10px;
    padding: 12px 20px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(37,99,235,0.35), var(--shadow-md);
    transition: all 0.2s;
    display: flex; align-items: center; gap: 8px;
    letter-spacing: -0.2px;
  }
  .pro-fab:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(37,99,235,0.4); }

  /* ── LOADING ── */
  .pro-loading {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 100vh;
    gap: 20px;
    background: var(--bg);
    position: relative; z-index: 10;
  }
  .pro-loading-title {
    font-family: 'DM Serif Display', serif;
    font-size: 26px;
    color: var(--text);
    letter-spacing: -0.5px;
  }
  .pro-loading-sub {
    font-size: 14px;
    color: var(--subtext);
  }
  .pro-loading-bar {
    width: 240px; height: 4px;
    background: var(--border);
    border-radius: 4px;
    overflow: hidden;
  }
  .pro-loading-fill {
    height: 100%;
    background: var(--accent);
    animation: proLoad 1.8s ease-in-out infinite alternate;
    border-radius: 4px;
  }
  @keyframes proLoad { 0% { width: 8%; } 100% { width: 90%; } }

  /* ── RESULT OVERLAY ── */
  .pro-result-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center;
    z-index: 500;
    animation: proOverlayIn 0.3s ease;
  }
  @keyframes proOverlayIn { from { opacity: 0; } to { opacity: 1; } }

  .pro-result-card {
    width: 90%; max-width: 460px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 40px 36px;
    text-align: center;
    box-shadow: var(--shadow-lg);
    animation: proResultIn 0.5s cubic-bezier(0.34,1.4,0.64,1);
  }
  @keyframes proResultIn { 0% { transform: scale(0.9) translateY(20px); opacity: 0; } 100% { transform: scale(1) translateY(0); opacity: 1; } }

  .pro-result-icon { font-size: 52px; margin-bottom: 16px; display: block; }
  .pro-result-title {
    font-family: 'DM Serif Display', serif;
    font-size: 26px;
    color: var(--text);
    margin-bottom: 4px;
    letter-spacing: -0.5px;
  }
  .pro-result-sub { font-size: 13px; color: var(--subtext); margin-bottom: 28px; }

  .pro-stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 24px; }
  .pro-stat-box {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 14px;
    text-align: left;
  }
  .pro-stat-label { font-size: 11px; font-weight: 500; color: var(--muted); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
  .pro-stat-val { font-family: 'DM Serif Display', serif; font-size: 22px; color: var(--text); letter-spacing: -0.3px; }

  .pro-result-redirect { font-size: 12px; color: var(--muted); }

  /* ── SCORE RING ── */
  .score-ring-wrap {
    display: flex; align-items: center; justify-content: center;
    margin: 20px 0;
    position: relative;
  }
  .score-ring-label {
    position: absolute;
    font-family: 'DM Serif Display', serif;
    font-size: 28px;
    color: var(--text);
  }

  /* ── TONE INDICATOR ── */
  .audio-indicator {
    position: fixed; bottom: 24px; left: 24px;
    z-index: 200;
    display: flex; align-items: center; gap: 8px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 14px;
    box-shadow: var(--shadow-sm);
    font-size: 11px;
    color: var(--subtext);
    transition: opacity 0.5s;
  }
  .audio-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); animation: audioPulse 0.6s ease-out forwards; }
  @keyframes audioPulse { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(2.5); opacity: 0; } }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 4px; }
`;

// ─── UTILITY ─────────────────────────────────────────────────────────────────
const pad2 = n => String(n).padStart(2, '0');
const fmtTime = s => `${pad2(Math.floor(s / 60))}:${pad2(s % 60)}`;
const KEYS = ['A', 'B', 'C', 'D'];
const COLORS = ['#ff006e','#00f5ff','#ffbe0b','#06d6a0','#a855f7','#f97316'];

function correctIdx(ca) {
  if (typeof ca === 'number') return ca;
  const s = String(ca).toUpperCase().trim();
  return { A:0, B:1, C:2, D:3 }[s] ?? Number(s);
}

function mkStars(n = 80) {
  return Array.from({ length: n }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    d: 2 + Math.random() * 4,
    delay: Math.random() * 4,
    op: 0.3 + Math.random() * 0.7,
  }));
}

// ─── PARTICLES (Arcade only) ─────────────────────────────────────────────────
let _pid = 0;
function useParticles() {
  const [particles, setParticles] = useState([]);

  const spawnCorrect = useCallback(() => {
    const now = Date.now();
    const px = Array.from({ length: 40 }, (_, i) => ({
      id: _pid++, type: 'px',
      x: 20 + Math.random() * 60, y: -5 - Math.random() * 10,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      w: 6 + Math.random() * 10, h: 6 + Math.random() * 10,
      dur: 1800 + Math.random() * 1400, born: now,
    }));
    const em = ['⭐','🎯','⚡','🌟','💫'].map((e, i) => ({
      id: _pid++, type: 'emoji', char: e,
      x: 25 + Math.random() * 50, y: 50 + Math.random() * 30,
      dur: 1200 + i * 200, born: now,
    }));
    setParticles(p => [...p, ...px, ...em]);
    setTimeout(() => setParticles(p => p.filter(x => Date.now() - x.born < x.dur + 200)), 3600);
  }, []);

  const spawnWrong = useCallback(() => {
    const now = Date.now();
    const em = [{ id: _pid++, type: 'emoji', char: '💀', x: 48, y: 48, dur: 900, born: now }];
    setParticles(p => [...p, ...em]);
    setTimeout(() => setParticles(p => p.filter(x => Date.now() - x.born < x.dur + 200)), 1200);
  }, []);

  return { particles, spawnCorrect, spawnWrong };
}

// ─── COMBO FLASH ─────────────────────────────────────────────────────────────
function ComboFlash({ text }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => { const t = setTimeout(() => setVisible(false), 900); return () => clearTimeout(t); }, []);
  if (!visible) return null;
  return <div className="combo-flash">{text}</div>;
}

// ─── AUDIO ENGINE ─────────────────────────────────────────────────────────────
// Arcade: chiptune bleeps
function playArcadeTone(freqs, gain = 0.15, descend = false) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.frequency.value = f;
      g.gain.setValueAtTime(gain, ctx.currentTime + i * 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.1 + 0.25);
      osc.start(ctx.currentTime + i * 0.1);
      osc.stop(ctx.currentTime + i * 0.1 + 0.25);
    });
  } catch {}
}

// Professional: subtle sine tones, softer envelope
function playProTone(type) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (type === 'correct') {
      // Clean major third chord — pleasant, understated
      [[523.25, 0], [659.25, 0.06], [783.99, 0.12]].forEach(([freq, delay]) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0, ctx.currentTime + delay);
        g.gain.linearRampToValueAtTime(0.09, ctx.currentTime + delay + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.5);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.55);
      });
    } else if (type === 'wrong') {
      // Minor descending two-note nudge
      [[329.63, 0], [261.63, 0.1]].forEach(([freq, delay]) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0, ctx.currentTime + delay);
        g.gain.linearRampToValueAtTime(0.07, ctx.currentTime + delay + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.4);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.45);
      });
    } else if (type === 'submit') {
      // Ascending 4-note resolution
      [392, 523.25, 659.25, 783.99].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(g); g.connect(ctx.destination);
        const t = ctx.currentTime + i * 0.09;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.08, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
        osc.start(t);
        osc.stop(t + 0.5);
      });
    }
  } catch {}
}

// ─── AUDIO INDICATOR (Pro only) ──────────────────────────────────────────────
function AudioIndicator({ text }) {
  const [show, setShow] = useState(true);
  useEffect(() => { const t = setTimeout(() => setShow(false), 1200); return () => clearTimeout(t); }, [text]);
  if (!show) return null;
  return (
    <div className="audio-indicator" style={{ opacity: show ? 1 : 0 }}>
      <div className="audio-dot" />
      <span>{text}</span>
    </div>
  );
}

// ─── SCORE RING (Pro result) ──────────────────────────────────────────────────
function ScoreRing({ score }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;
  const color = score >= 80 ? '#16a34a' : score >= 50 ? '#2563eb' : '#dc2626';
  return (
    <div className="score-ring-wrap">
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={r} fill="none" stroke="#e4e2dc" strokeWidth="8" />
        <circle
          cx="70" cy="70" r={r}
          fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={`${fill} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 70 70)"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
      </svg>
      <div className="score-ring-label">{score?.toFixed(0)}%</div>
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const DailyAssessment = () => {
  // ── grade detection ──
  // grade is fetched from user session/profile; defaults to 0 (shows loading)
  // You can replace this with your actual grade source (e.g., from props, context, or API)
  const [userGrade, setUserGrade] = useState(null);

  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [startTime] = useState(Date.now());
  const [elapsed, setElapsed] = useState(0);
  const [streak, setStreak] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [combo, setCombo] = useState(0);
  const [comboFlash, setComboFlash] = useState(null);
  const [xp, setXp] = useState(0);
  const [stars] = useState(mkStars);
  const [audioHint, setAudioHint] = useState(null);
  const { particles, spawnCorrect, spawnWrong } = useParticles();

  const cardRef = useRef(null);
  const cdRef = useRef(null);
  const redirectRef = useRef(null);

  // derived
  const isArcade = userGrade !== null && userGrade >= 1 && userGrade <= 5;
  const isPro    = userGrade !== null && userGrade >= 6 && userGrade <= 12;

  // ── timer ──
  useEffect(() => {
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [startTime]);

  // ── load ──
  useEffect(() => {
    loadUserGrade();
    loadStreak();
    loadQuestions();
  }, []);

  const loadUserGrade = async () => {
    try {
      const r = await fetch('/api/profile');
      const d = await r.json();
      setUserGrade(d.user.grade);
    } catch {
      setUserGrade(9); // default to pro if unknown
    }
  };

  const loadStreak = async () => {
    try {
      const r = await fetch('/api/daily-assessment/status');
      const d = await r.json();
      setStreak(d.streak ?? 0);
    } catch { setStreak(0); }
  };

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const r = await fetch('/api/generate-daily-questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ validation_mode: 'strict' }),
      });
      const d = await r.json();
      if (d.redirect) { window.location.href = d.redirect; return; }
      if (d.success) { setQuestions(d.questions); setLoading(false); }
      else { setError(d.message || 'Failed to load'); setLoading(false); }
    } catch { setError('Failed to load questions. Please refresh.'); setLoading(false); }
  };

  // ── answer ──
  const selectAnswer = (qIdx, aIdx) => {
    if (answers[qIdx] !== undefined) return;
    const q = questions[qIdx];
    const ok = aIdx === correctIdx(q.correct_answer);
    const newAnswers = { ...answers, [qIdx]: aIdx };
    setAnswers(newAnswers);

    if (ok) {
      const newCombo = combo + 1;
      setCombo(newCombo);

      if (isArcade) {
        const gained = 100 * (newCombo >= 3 ? 2 : 1);
        setXp(x => x + gained);
        spawnCorrect();
        setComboFlash(newCombo >= 3 ? `🔥 ${newCombo}x COMBO! +${gained}XP` : `+${gained} XP`);
        setTimeout(() => setComboFlash(null), 950);
        playArcadeTone([523, 659, 784], 0.1);
      } else {
        playProTone('correct');
        setAudioHint('Correct');
        setTimeout(() => setAudioHint(null), 1300);
      }
    } else {
      setCombo(0);
      if (isArcade) {
        spawnWrong();
        playArcadeTone([150, 100], 0.12, true);
      } else {
        playProTone('wrong');
        setAudioHint('Incorrect');
        setTimeout(() => setAudioHint(null), 1300);
      }
      if (cardRef.current) {
        cardRef.current.classList.add('shake');
        setTimeout(() => cardRef.current?.classList.remove('shake'), 450);
      }
    }

    setTimeout(() => {
      if (qIdx < questions.length - 1) {
        setCurrent(qIdx + 1);
        setCountdown(3);
      } else {
        submitAssessment(newAnswers);
      }
    }, 2200);
  };

  // ── countdown ──
  useEffect(() => {
    if (answers[current] === undefined) return;
    if (current >= questions.length - 1) return;
    setCountdown(3);
    if (cdRef.current) clearInterval(cdRef.current);
    let c = 3;
    cdRef.current = setInterval(() => {
      c--;
      setCountdown(c);
      if (c <= 0) clearInterval(cdRef.current);
    }, 1000);
    return () => clearInterval(cdRef.current);
  }, [current, answers, questions.length]);

  // ── submit ──
  const submitAssessment = async (finalAnswers = answers) => {
    if (submitted) return;
    setSubmitted(true);
    const fmt = {};
    Object.entries(finalAnswers).forEach(([k, v]) => fmt[k] = v);
    try {
      const r = await fetch('/api/submit-daily-assessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: fmt, time_spent: elapsed }),
      });
      const d = await r.json();
      if (d.success) {
        setResult(d);
        if (isArcade) { spawnCorrect(); spawnCorrect(); }
        else { playProTone('submit'); }
        let rc = 5;
        const iv = setInterval(() => {
          rc--;
          if (redirectRef.current) redirectRef.current.textContent = rc;
          if (rc <= 0) { clearInterval(iv); window.location.href = '/dashboard'; }
        }, 1000);
      } else {
        setError(d.message || 'Submission failed');
        setSubmitted(false);
      }
    } catch { setError('Submission failed. Try again.'); setSubmitted(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  const q = questions[current];
  const userAns = answers[current];
  const answered = userAns !== undefined;
  const level = Math.floor(xp / 500) + 1;

  let opts = [];
  if (q?.options) {
    opts = Array.isArray(q.options)
      ? q.options.slice(0, 4)
      : ['A','B','C','D'].map(k => q.options[k] ?? '');
  }

  // ══════════════════════════════════════════════════
  // ARCADE UI — Grades 1–5
  // ══════════════════════════════════════════════════
  const renderArcade = () => {
    if (loading) return (
      <>
        <style>{ARCADE_CSS}</style>
        <div className="da-root">
          <div className="stars">{stars.map(s => <div key={s.id} className="star" style={{ left: s.x+'%', top: s.y+'%', '--d': s.d+'s', '--delay': s.delay+'s', '--op': s.op }} />)}</div>
          <div className="loading-screen">
            <div className="loading-title">DAILY MISSION</div>
            <div className="loading-sub">LOADING CHALLENGE<span className="loading-dots" /></div>
            <div className="loading-bar-wrap"><div className="loading-bar-fill" /></div>
            <div style={{ fontFamily:"'Press Start 2P'", fontSize:8, color:'var(--subtext)' }}>PREPARE YOURSELF</div>
          </div>
        </div>
      </>
    );

    if (error) return (
      <>
        <style>{ARCADE_CSS}</style>
        <div className="da-root" style={{ alignItems:'center', justifyContent:'center', gap:20, padding:30, textAlign:'center' }}>
          <div style={{ fontFamily:"'Press Start 2P'", fontSize:12, color:'var(--accent2)' }}>ERROR</div>
          <div style={{ fontFamily:"'Press Start 2P'", fontSize:8, color:'var(--subtext)', lineHeight:2 }}>{error}</div>
          <button onClick={() => window.location.reload()} style={{ fontFamily:"'Press Start 2P'", fontSize:8, padding:'10px 18px', background:'var(--accent1)', color:'var(--bg)', border:'none', borderRadius:6, cursor:'pointer', boxShadow:'var(--glow1)' }}>RETRY</button>
        </div>
      </>
    );

    return (
      <>
        <style>{ARCADE_CSS}</style>
        <div className="da-root">
          <div className="stars">{stars.map(s => <div key={s.id} className="star" style={{ left: s.x+'%', top: s.y+'%', '--d': s.d+'s', '--delay': s.delay+'s', '--op': s.op }} />)}</div>
          <div className="particle-layer">
            {particles.map(p => p.type === 'px'
              ? <div key={p.id} className="px-particle" style={{ left: p.x+'%', top: p.y+'%', width: p.w+'px', height: p.h+'px', background: p.color, animationDuration: p.dur+'ms' }} />
              : <div key={p.id} className="px-emoji" style={{ left: p.x+'%', top: p.y+'%', animationDuration: p.dur+'ms' }}>{p.char}</div>
            )}
          </div>
          {comboFlash && <ComboFlash key={comboFlash + Date.now()} text={comboFlash} />}

          <div className="da-hud">
            <div className="da-logo">DAILY MISSION</div>
            <div className="da-hud-stats">
              <div className="hud-chip" style={{ '--chip-color': 'var(--accent1)' }}><div><div className="label">TIME</div><div className="value">⏱ {fmtTime(elapsed)}</div></div></div>
              <div className="hud-chip" style={{ '--chip-color': 'var(--accent2)' }}><div><div className="label">STREAK</div><div className="value">🔥 {streak}d</div></div></div>
              <div className="hud-chip" style={{ '--chip-color': 'var(--accent3)' }}><div><div className="label">COMBO</div><div className="value">⚡ {combo}x</div></div></div>
              <div className="level-badge">LVL {level}</div>
            </div>
            <button className="help-btn" onClick={() => alert('Answer all questions to complete the mission!')}>? HELP</button>
          </div>

          <div className="xp-bar-wrap">
            <div className="xp-label">XP</div>
            <div className="xp-track"><div className="xp-fill" style={{ width: `${Math.min(100, (xp % 500) / 5)}%` }} /></div>
            <div className="xp-count">{xp} / {level * 500}</div>
          </div>

          <div className="da-arena">
            <div style={{ display:'flex', gap:8, marginBottom:20, alignSelf:'flex-start', maxWidth:680, width:'100%' }}>
              {questions.map((_, i) => (
                <div key={i} style={{
                  flex:1, height:6, borderRadius:2,
                  background: i < current ? 'var(--accent4)' : i === current ? 'var(--accent1)' : 'var(--border)',
                  boxShadow: i === current ? 'var(--glow1)' : i < current ? 'var(--glow4)' : 'none',
                  transition:'all 0.4s ease',
                }} />
              ))}
            </div>

            {q && (
              <div className="q-card" ref={cardRef} key={current}>
                <div className="q-card-corner tl" /><div className="q-card-corner tr" />
                <div className="q-card-corner bl" /><div className="q-card-corner br" />
                <div className="q-meta">
                  <div className="q-num">Q{current + 1} / {questions.length}</div>
                  {q.concept && <div className="q-concept">{q.concept}</div>}
                </div>
                <div className="q-text">{q.question}</div>
                <div className="options-grid">
                  {opts.map((opt, i) => {
                    const ci = correctIdx(q.correct_answer);
                    let cls = '';
                    if (answered) {
                      if (i === ci) cls = 'correct';
                      else if (i === userAns && i !== ci) cls = 'wrong';
                    }
                    return (
                      <button key={i} className={`opt-btn ${cls}`} disabled={answered} onClick={() => selectAnswer(current, i)}>
                        <div className="opt-key">{KEYS[i]}</div>
                        <span style={{ flex:1 }}>{opt || `Option ${KEYS[i]}`}</span>
                        {answered && i === ci && <span style={{ color:'var(--correct)', fontSize:16 }}>✓</span>}
                        {answered && i === userAns && i !== ci && <span style={{ color:'var(--wrong)', fontSize:16 }}>✗</span>}
                      </button>
                    );
                  })}
                </div>
                {answered && (
                  <div className={`feedback-panel ${userAns === correctIdx(q.correct_answer) ? 'correct-fb' : 'wrong-fb'}`}>
                    <div className="fb-title">
                      {userAns === correctIdx(q.correct_answer)
                        ? `🎯 CORRECT! ${combo >= 3 ? `${combo}x COMBO BONUS!` : ''}`
                        : '💀 WRONG ANSWER'}
                    </div>
                    {q.explanation && <div className="fb-explain">💡 {q.explanation}</div>}
                    {current < questions.length - 1 && (
                      <div className="next-row">
                        <div className="countdown-pill">NEXT IN {countdown}s...</div>
                        <button className="next-btn" onClick={() => { if(cdRef.current) clearInterval(cdRef.current); setCurrent(c => c+1); setCountdown(3); }}>SKIP →</button>
                      </div>
                    )}
                    {current === questions.length - 1 && (
                      <div className="countdown-pill" style={{ marginTop:12, textAlign:'center' }}>🚀 SUBMITTING...</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <button className="fab-submit" onClick={() => submitAssessment()}>🚀 SUBMIT NOW</button>

          {result && (
            <div className="result-overlay">
              <div className="result-card">
                <div className="result-inner">
                  <span className="result-icon">{result.score >= 80 ? '🏆' : result.score >= 50 ? '⭐' : '💀'}</span>
                  <div className="result-title">MISSION {result.score >= 50 ? 'COMPLETE' : 'FAILED'}</div>
                  <div className="result-sub">{new Date().toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' })}</div>
                  <div className="stat-grid">
                    <div className="stat-box"><div className="s-label">SCORE</div><div className="s-val">{result.score?.toFixed(0)}%</div></div>
                    <div className="stat-box"><div className="s-label">CORRECT</div><div className="s-val">{result.correct}/{result.total}</div></div>
                    <div className="stat-box"><div className="s-label">TIME</div><div className="s-val">{fmtTime(elapsed)}</div></div>
                    <div className="stat-box"><div className="s-label">STREAK</div><div className="s-val">🔥 {result.streak}d</div></div>
                  </div>
                  <div style={{ fontFamily:"'Press Start 2P'", fontSize:10, color:'var(--accent3)', margin:'10px 0' }}>XP EARNED: +{xp}</div>
                  <div className="result-redirect">RETURNING TO BASE IN <span ref={redirectRef}>5</span>s...</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  // ══════════════════════════════════════════════════
  // PROFESSIONAL UI — Grades 6–12
  // ══════════════════════════════════════════════════
  const renderPro = () => {
    const progress = questions.length ? ((current + 1) / questions.length) * 100 : 0;

    if (loading) return (
      <>
        <style>{PRO_CSS}</style>
        <div className="pro-root">
          <div className="pro-loading">
            <div className="pro-loading-title">Daily Assessment</div>
            <div className="pro-loading-sub">Preparing your questions…</div>
            <div className="pro-loading-bar"><div className="pro-loading-fill" /></div>
          </div>
        </div>
      </>
    );

    if (error) return (
      <>
        <style>{PRO_CSS}</style>
        <div className="pro-root" style={{ alignItems:'center', justifyContent:'center', gap:14, padding:30, textAlign:'center', minHeight:'100vh', display:'flex', flexDirection:'column', position:'relative', zIndex:1 }}>
          <div style={{ fontFamily:"'DM Serif Display', serif", fontSize:22, color:'var(--error)' }}>Unable to Load</div>
          <div style={{ fontSize:14, color:'var(--subtext)', maxWidth:320 }}>{error}</div>
          <button onClick={() => window.location.reload()} style={{ fontFamily:"'DM Sans', sans-serif", fontSize:13, fontWeight:600, padding:'10px 20px', background:'var(--accent)', color:'white', border:'none', borderRadius:8, cursor:'pointer' }}>Try Again</button>
        </div>
      </>
    );

    return (
      <>
        <style>{PRO_CSS}</style>
        <div className="pro-root">

          {/* Nav */}
          <div className="pro-nav">
            <div className="pro-brand">Assessment<span>.</span></div>
            <div className="pro-nav-divider" />
            <div className="pro-nav-meta">
              <div className="pro-badge accent">
                <div className="dot" />
                Q {current + 1} of {questions.length}
              </div>
              <div className="pro-badge">
                ⏱ {fmtTime(elapsed)}
              </div>
              <div className="pro-badge warn">
                🔥 {streak} day streak
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="pro-progress-bar">
            <div className="pro-progress-fill" style={{ width: `${progress}%` }} />
          </div>

          {/* Main */}
          <div className="pro-main">

            {/* Stepper */}
            {questions.length > 0 && (
              <div className="pro-stepper">
                {questions.map((_, i) => (
                  <div key={i} className={`pro-step-item ${i < current ? 'done' : i === current ? 'active' : ''}`}>
                    <div className="pro-step-dot">
                      {i < current ? '✓' : i + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Question card */}
            {q && (
              <div className="pro-card" ref={cardRef} key={current}>
                <div className="pro-q-header">
                  <div className="pro-q-label">Question {current + 1}</div>
                  {q.concept && <div className="pro-q-concept">{q.concept}</div>}
                </div>

                <div className="pro-q-text">{q.question}</div>

                <div className="pro-options">
                  {opts.map((opt, i) => {
                    const ci = correctIdx(q.correct_answer);
                    let cls = '';
                    if (answered) {
                      if (i === ci) cls = 'correct';
                      else if (i === userAns && i !== ci) cls = 'wrong';
                    }
                    return (
                      <button key={i} className={`pro-opt ${cls}`} disabled={answered} onClick={() => selectAnswer(current, i)}>
                        <div className="pro-opt-key">{KEYS[i]}</div>
                        <span style={{ flex:1, lineHeight:1.5 }}>{opt || `Option ${KEYS[i]}`}</span>
                        {answered && i === ci && <span className="pro-opt-check" style={{ color:'var(--success)' }}>✓</span>}
                        {answered && i === userAns && i !== ci && <span className="pro-opt-check" style={{ color:'var(--error)' }}>✗</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Feedback */}
                {answered && (
                  <div className={`pro-feedback ${userAns === correctIdx(q.correct_answer) ? 'correct-fb' : 'wrong-fb'}`}>
                    <div className="pro-fb-title">
                      {userAns === correctIdx(q.correct_answer) ? 'Correct' : 'Incorrect'}
                    </div>
                    {q.explanation && <div className="pro-fb-explain">{q.explanation}</div>}

                    {current < questions.length - 1 && (
                      <div className="pro-next-row">
                        <div className="pro-countdown">Next question in {countdown}s</div>
                        <button className="pro-next-btn" onClick={() => { if(cdRef.current) clearInterval(cdRef.current); setCurrent(c => c+1); setCountdown(3); }}>
                          Continue →
                        </button>
                      </div>
                    )}
                    {current === questions.length - 1 && (
                      <div style={{ marginTop:12, fontSize:12, color:'var(--muted)', fontWeight:500 }}>
                        Submitting your responses…
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Floating submit */}
          <button className="pro-fab" onClick={() => submitAssessment()}>
            Submit Assessment →
          </button>

          {/* Audio indicator */}
          {audioHint && <AudioIndicator key={audioHint + Date.now()} text={audioHint} />}

          {/* Result overlay */}
          {result && (
            <div className="pro-result-overlay">
              <div className="pro-result-card">
                <span className="pro-result-icon">
                  {result.score >= 80 ? '🎓' : result.score >= 50 ? '📘' : '📖'}
                </span>
                <div className="pro-result-title">
                  {result.score >= 80 ? 'Excellent Work' : result.score >= 50 ? 'Assessment Complete' : 'Keep Practicing'}
                </div>
                <div className="pro-result-sub">{new Date().toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' })}</div>

                <ScoreRing score={result.score ?? 0} />

                <div className="pro-stat-grid">
                  <div className="pro-stat-box"><div className="pro-stat-label">Correct</div><div className="pro-stat-val">{result.correct}/{result.total}</div></div>
                  <div className="pro-stat-box"><div className="pro-stat-label">Time Taken</div><div className="pro-stat-val">{fmtTime(elapsed)}</div></div>
                  <div className="pro-stat-box"><div className="pro-stat-label">Streak</div><div className="pro-stat-val">{result.streak} days</div></div>
                  <div className="pro-stat-box"><div className="pro-stat-label">Avg / Question</div><div className="pro-stat-val">{questions.length ? fmtTime(Math.floor(elapsed / questions.length)) : '--'}</div></div>
                </div>

                <div className="pro-result-redirect">
                  Returning to dashboard in <span ref={redirectRef}>5</span>s
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  // ── Grade not yet loaded ──
  if (userGrade === null) return (
    <div style={{ minHeight:'100vh', background:'#f8f7f4', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'DM Sans, sans-serif', color:'#6b6860', fontSize:14 }}>
      Loading…
    </div>
  );

  return isArcade ? renderArcade() : renderPro();
};

export default DailyAssessment;